import React, { useEffect, useState } from "react";
import { Navigate, Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { tokenStore, authApi } from "./api";
import { LayoutDashboard, Users, ScrollText, Flag, LogOut, ShieldCheck, ChevronRight, Monitor, FileText, BookOpen, HelpCircle, BookMarked, FlaskConical, Newspaper, ClipboardList, Quote, Pill, Stethoscope, Microscope, Syringe, Calendar, Network, Inbox, Video, Download, Image as ImageIcon, Book, MessageSquareQuote, Mic, Home, Menu as MenuIcon, ArrowRightLeft, Mail, Settings as SettingsIcon, Search as SearchIcon, Presentation, Calculator, Sparkles, Cpu, Layers, UserCheck, Lock } from "lucide-react";
import { ErrorBoundary } from "./ErrorBoundary";
import CommandPalette from "./CommandPalette";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { section: "Content" },
  { to: "/admin/content/articles", label: "Articles", icon: FileText },
  { to: "/admin/content/conditions", label: "Conditions", icon: BookOpen },
  { to: "/admin/content/faqs", label: "FAQs", icon: HelpCircle },
  { to: "/admin/content/guidelines", label: "Guidelines", icon: BookMarked },
  { to: "/admin/content/research-papers", label: "Research Papers", icon: FlaskConical },
  { to: "/admin/content/research-updates", label: "Research Updates", icon: Newspaper },
  { to: "/admin/content/evidence-summaries", label: "Evidence Summaries", icon: ClipboardList },
  { to: "/admin/content/references", label: "References", icon: Quote },
  { to: "/admin/insights-pending", label: "Insights · Pending", icon: ShieldCheck },
  { to: "/admin/insights-private", label: "Insights · Private", icon: Lock },
  { section: "Knowledge Graph" },
  { to: "/admin/content/treatments", label: "Treatments", icon: Syringe },
  { to: "/admin/content/symptoms", label: "Symptoms", icon: Stethoscope },
  { to: "/admin/content/investigations", label: "Investigations", icon: Microscope },
  { to: "/admin/content/medicines", label: "Medicines", icon: Pill },
  { to: "/admin/content/trimesters", label: "Trimesters", icon: Calendar },
  { to: "/admin/suggestions", label: "Suggestions Inbox", icon: Inbox },
  { to: "/admin/graph-explorer", label: "Graph Explorer", icon: Network },
  { section: "Media Library" },
  { to: "/admin/content/videos", label: "Videos", icon: Video },
  { to: "/admin/content/patient-guides", label: "Patient Guides", icon: Download },
  { to: "/admin/content/infographics", label: "Infographics", icon: ImageIcon },
  { to: "/admin/content/books", label: "Books", icon: Book },
  { to: "/admin/content/testimonials", label: "Testimonials", icon: MessageSquareQuote },
  { to: "/admin/content/media-appearances", label: "Media Appearances", icon: Mic },
  { to: "/admin/derivative-queue", label: "Derivative Queue", icon: Layers },
  { section: "AI & Tools" },
  { to: "/admin/content/teaching-slides", label: "Teaching Slides", icon: Presentation },
  { to: "/admin/content/clinical-tools", label: "Clinical Tools", icon: Calculator },
  { to: "/admin/content/ai-prompts", label: "AI Prompt Library", icon: Sparkles },
  { to: "/admin/content/ai-models", label: "AI Model Registry", icon: Cpu },
  { section: "Doctor Portal" },
  { to: "/admin/doctor-accounts", label: "Doctor Accounts", icon: UserCheck },
  { section: "Site" },
  { to: "/admin/homepage", label: "Homepage", icon: Home },
  { to: "/admin/navigation", label: "Navigation", icon: MenuIcon },
  { to: "/admin/redirects", label: "Redirects", icon: ArrowRightLeft },
  { to: "/admin/enquiries", label: "Enquiries", icon: Mail },
  { to: "/admin/seo", label: "SEO & Structured Data", icon: SearchIcon },
  { section: "Administration" },
  { to: "/admin/users", label: "Users & Roles", icon: Users },
  { to: "/admin/sessions", label: "Sessions & Devices", icon: Monitor },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/feature-flags", label: "Feature Flags", icon: Flag },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export function ProtectedAdmin() {
  const [checking, setChecking] = useState(true);
  const [ok, setOk] = useState(false);
  useEffect(() => {
    if (!tokenStore.access) { setChecking(false); setOk(false); return; }
    authApi.me().then(u => { tokenStore.set({ user: u }); setOk(true); }).catch(() => setOk(false)).finally(() => setChecking(false));
  }, []);
  if (checking) return <div className="min-h-screen flex items-center justify-center bg-bone"><div className="mono text-xs uppercase tracking-widest text-stone2">Verifying session…</div></div>;
  if (!ok) return <Navigate to="/admin/login" replace />;
  return <AdminShell><ErrorBoundary><Outlet/></ErrorBoundary></AdminShell>;
}

function AdminShell({ children }) {
  const loc = useLocation();
  const nav = useNavigate();
  const user = tokenStore.user;
  const logout = async () => { try { await authApi.logout(); } catch {} tokenStore.clear(); nav("/admin/login"); };
  return (
    <div className="min-h-screen bg-bone flex" data-testid="admin-shell">
      <meta name="robots" content="noindex,nofollow"/>
      <aside className="w-64 bg-navy-deep text-white flex flex-col">
        <div className="px-6 py-6 border-b border-white/10">
          <Link to="/admin" className="block">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige-soft">Admin CMS</div>
            <div className="font-serif text-2xl mt-1">Dr. Tania Gurdip Singh</div>
          </Link>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.map((n, i) => {
            if (n.section) return <div key={`s${i}`} className="mono text-[9px] uppercase tracking-[0.2em] text-white/40 px-6 pt-4 pb-1">{n.section}</div>;
            const active = n.exact ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            const Icon = n.icon;
            return (
              <Link key={n.to} to={n.to} data-testid={`anav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
                className={`flex items-center gap-3 px-6 py-2.5 text-sm border-l-2 transition-colors ${active ? "border-terracotta bg-white/5 text-white" : "border-transparent text-white/70 hover:text-white hover:bg-white/5"}`}>
                <Icon size={16} strokeWidth={1.5}/>{n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/60">Signed in as</div>
          <div className="text-sm font-medium mt-1">{user?.name}</div>
          <div className="mono text-[10px] uppercase tracking-widest text-prestige-soft mt-0.5">{user?.role_label}</div>
          <button onClick={logout} data-testid="admin-logout" className="mt-3 w-full flex items-center justify-center gap-2 border border-white/25 hover:bg-white/10 text-white text-xs uppercase tracking-widest mono py-2">
            <LogOut size={14} strokeWidth={1.5}/> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white border-b border-line px-8 py-4 flex items-center justify-between gap-3 mono text-[11px] uppercase tracking-widest text-stone2">
          <div className="flex items-center gap-2">
            <ShieldCheck size={14} strokeWidth={1.5} className="text-prestige"/> Confidential admin area · noindex
          </div>
          <CommandPalette/>
        </div>
        <main className="flex-1 p-8 overflow-x-auto">{children}</main>
      </div>
    </div>
  );
}

export function Breadcrumbs({ items = [] }) {
  return (
    <div className="flex items-center gap-2 text-xs mono uppercase tracking-widest text-stone2 mb-6">
      {items.map((it, i) => {
        const label = typeof it === "string" ? it : (it?.label || "");
        return (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight size={12}/>}
            <span className={i === items.length - 1 ? "text-navy" : ""}>{label}</span>
          </React.Fragment>
        );
      })}
    </div>
  );
}
